<template>
  <q-dialog
    ref="dialog"
    :maximized="$q.platform.is.mobile"
    @hide="onDialogHide"
  >
    <q-card
      id="multiple-trials-dialog"
      class="q-pa-sm"
      data-e2e="dialog-add-multiple-trials"
    >
      <q-card-section class="row">
        <div class="text-h6 col ellipsis">
          {{ dialogTitle }}
        </div>
        <q-btn
          v-close-popup
          flat
          round
          dense
          icon="close"
        />
      </q-card-section>
      <q-form @submit="onSubmitForm">
        <q-card-section class="q-pt-none">
          <hr-form-section-header
            v-if="showAddBehavior"
            :label="$t('sessions.trialOverview.modals.addMultipleTrials.title')"
            class="q-pb-md"
          />
          <component :is="showAddBehavior ? 'hr-content-card' : 'div'">
            <q-input
              v-if="!hasMultiplePromptLevels"
              ref="trialsInput"
              v-model.number="trialsNumber"
              min="0"
              :label="$t('sessions.trialOverview.modals.addMultipleTrials.trialsInputLabel')"
              outlined
              type="number"
              required
              autofocus
              pattern="[0-9]*"
              data-e2e="input-trials-number"
              :rules="[
                val => (val >= minValue)
                  || $t('sessions.trialOverview.modals.addMultipleTrials.trialsInputValidator')
              ]"
            />
            <div
              class="q-mb-sm"
            >
              <q-input
                v-if="showDuration || isDurationData"
                v-model="durationTime"
                dense
                outlined
                :label="$t('sessions.trialOverview.durationInputLabel')"
                mask="#:##:##"
                autofocus
              />
            </div>
            <q-list
              v-if="hasMultiplePromptLevels"
              style="min-width: 100px"
            >
              <q-item
                v-for="editPromptLevel in editTrialsPerPromptLevel"
                :key="editPromptLevel.promptLevel.id"
                clickable
                :style="getPromptLevelStyle(editPromptLevel.promptLevel)"
                class="prompt-level row items-center"
                @click="onAddData(editPromptLevel.promptLevel)"
              >
                <q-item-section>{{ editPromptLevel.promptLevel.trialOutcome }}</q-item-section>
                <q-item-section>
                  <q-input
                    v-model.number="editPromptLevel.value"
                    min="0"
                    outlined
                    type="number"
                    dense
                    required
                    autofocus
                    pattern="[0-9]*"
                    hide-bottom-space
                    class="bg-white"
                  />
                </q-item-section>
              </q-item>
            </q-list>
          </component>
          <behavior-occurrence-picker
            v-if="showAddBehavior"
            class="q-pt-md"
            @update="behaviorOccurrences = $event"
          />
        </q-card-section>
        <q-card-actions>
          <q-btn
            v-if="canDelete"
            flat
            :label="$t('common.buttonDelete')"
            color="negative"
            @click="onDelete"
          />
          <q-space />
          <q-btn
            v-close-popup
            flat
            :label="$t('common.buttonCancel')"
            color="secondary"
          />
          <q-btn
            flat
            :label="$t('common.buttonSave')"
            type="submit"
            color="primary"
            :disable="isSaveDisabled"
            data-e2e="button-save"
          />
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>

<script>
import BehaviorOccurrencePicker from 'components/Programs/BehaviorOccurrencePicker';
import { chain, get, isArray, map, sumBy } from 'lodash';
import { mapState } from 'pinia';
import { HrContentCard, HrFormSectionHeader } from 'src/design-system';
import Targets from 'src/mixins/Targets';
import Toolbox from 'src/mixins/Toolbox';
import { promptLevels } from 'src/store/promptLevels';

export default {
  name: 'AddMultipleTrials',

  components: { BehaviorOccurrencePicker, HrContentCard, HrFormSectionHeader },

  mixins: [Targets, Toolbox],

  props: {
    program: {
      type: Object,
      required: true,
    },
    promptLevelTemplate: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      default: null,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    duration: {
      type: Number,
      default: null,
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
    allowZero: {
      type: Boolean,
      default: false,
    },
    showDuration: {
      type: Boolean,
      default: false,
    },
    showAddBehavior: {
      type: Boolean,
      default: false,
    },
    /** Target the behavior occurrences are recorded against; only used with `showAddBehavior`. */
    targetLabel: {
      type: String,
      default: '',
    },
  },

  emits: ['ok', 'hide'],

  data() {
    return {
      editTrialsPerPromptLevel: {},

      durationTime: '0:00:00',

      behaviorOccurrences: [],
    };
  },

  computed: {
    ...mapState(promptLevels, ['activePromptLevels']),

    dialogTitle() {
      if (this.showAddBehavior && this.targetLabel) {
        return this.$t('run.collectData.addBehaviorDialog.title', { target: this.targetLabel });
      }

      return this.title || this.$t('sessions.trialOverview.modals.addMultipleTrials.title');
    }, // dialogTitle()

    isDurationData() {
      return get(this.program, ['data', 'dataType']) === 'Duration1.0';
    }, // isDurationData()

    programPrompts() {
      return get(this.promptLevelTemplate, 'promptLevels', []);
    }, // programPrompts()

    hasMultiplePromptLevels() {
      if (!isArray(this.programPrompts)) {
        return false;
      }

      return this.programPrompts.length !== 1;
    }, // hasMultiplePromptLevels()

    isSaveDisabled() {
      if (this.behaviorOccurrences.length > 0) {
        return false;
      }

      return !this.allowZero && (sumBy(this.editTrialsPerPromptLevel, 'value') === 0);
    }, // isSaveDisabled()

    minValue() {
      // A pending behavior occurrence is enough to save, as isSaveDisabled already allows.
      if (this.allowZero || this.behaviorOccurrences.length > 0) {
        return 0;
      }

      return 1;
    }, // minValue()

    trialsNumber: {
      get() {
        return chain(this.editTrialsPerPromptLevel).head().get('value').value();
      },
      set(value) {
        this.editTrialsPerPromptLevel = map(
          this.editTrialsPerPromptLevel,
          (promptLevelInfo) => ({ ...promptLevelInfo, value }),
        );
      },
    },

  },

  watch: {
    behaviorOccurrences() {
      // The rule only reruns when its own input changes, so clear the error minValue no longer backs.
      this.$refs.trialsInput?.validate();
    },
  },

  methods: {
    onSubmitForm() {
      this.$emit('ok', {
        data: this.editTrialsPerPromptLevel,
        duration: this.showDuration ? this.stringToDuration(this.durationTime) : null,
        delete: false,
        behaviorOccurrences: this.behaviorOccurrences,
      });
      this.hide();
    }, // onSubmitForm()

    onDelete() {
      this.$emit('ok', { data: null, duration: null, deleteTrial: true });
      this.hide();
    }, // onDelete()

    show() {
      this.$refs.dialog.show();
      this.initData();
    },

    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onAddData(value) {
      this.selectedPromptLevel = value;
    },

    onDialogHide() {
      this.$emit('hide');
    },

    initData() {
      this.editTrialsPerPromptLevel = chain(this.promptLevelTemplate)
        .get('promptLevels', [])
        .map((promptLevel) => ({ promptLevel, value: get(this.data, promptLevel.id, 0) }))
        .value();

      if (this.showDuration) {
        this.durationTime = this.formatDuration(this.duration);
      }
    }, // initData()

  },
};
</script>

<style scoped>
#multiple-trials-dialog {
  width: 400px;
}
</style>
